import { useEffect, useState } from "react";
import { ArrowRight, Sparkles, Brain } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSettings } from "@/lib/settings";

/**
 * Multi-slide hero. Falls back to single hero from settings if no slides.
 */
export default function HeroSlider() {
  const { settings } = useSettings();
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => { api.get("/slides").then(({ data }) => setSlides(data)).catch(() => {}); }, []);

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);

  const s = settings;
  if (!s) return null;

  // Fallback single hero when no slides added
  const effective = slides.length > 0 ? slides[idx] : {
    heading: s.hero_title,
    subheading: s.tagline,
    description: s.hero_subtitle,
    cta_text: s.hero_cta_primary,
    cta_link: s.hero_cta_primary_link,
    secondary_cta_text: "Explore Courses",
    secondary_cta_link: "/courses",
    image: "https://images.pexels.com/photos/30858451/pexels-photo-30858451.jpeg?w=800",
  };

  return (
    <section className="relative overflow-hidden pt-16 pb-24 lg:pt-24 lg:pb-32 grain" data-testid="hero-section">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 -right-20 w-96 h-96 bg-sky-100 rounded-full blur-3xl opacity-60"></div>
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-orange-100 rounded-full blur-3xl opacity-40"></div>
      </div>
      <div className="container-eq grid lg:grid-cols-12 gap-12 items-center relative">
        <div className="lg:col-span-7 fade-up" key={idx}>
          {effective.subheading && (
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-neutral-200 mb-6" data-testid="hero-badge">
              <span className="pulse-dot"></span>
              <span className="text-xs font-semibold tracking-widest uppercase">{effective.subheading}</span>
            </div>
          )}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-black leading-[1.05] tracking-tighter" data-testid="hero-title">
            {effective.heading}
          </h1>
          {effective.description && (
            <p className="mt-6 text-lg text-neutral-600 max-w-2xl leading-relaxed" data-testid="hero-subtitle">
              {effective.description}
            </p>
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            {effective.cta_text && (
              <a href={effective.cta_link || "#"} target={effective.cta_link?.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                 className="btn-primary-eq" data-testid="hero-cta-primary">
                {effective.cta_text} <ArrowRight className="w-4 h-4" />
              </a>
            )}
            {effective.secondary_cta_text && (
              effective.secondary_cta_link?.startsWith("http")
                ? <a href={effective.secondary_cta_link} target="_blank" rel="noreferrer" className="btn-outline-eq" data-testid="hero-cta-secondary">{effective.secondary_cta_text}</a>
                : <Link to={effective.secondary_cta_link || "/courses"} className="btn-outline-eq" data-testid="hero-cta-secondary">{effective.secondary_cta_text}</Link>
            )}
          </div>

          {slides.length > 1 && (
            <div className="mt-10 flex gap-2" data-testid="hero-dots">
              {slides.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)}
                  className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-sky-500" : "w-2 bg-neutral-300"}`} />
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-5 relative">
          <div className="relative rounded-3xl overflow-hidden tracing-border">
            <img src={effective.image} alt={effective.heading} className="w-full h-[480px] object-cover transition-opacity duration-500" data-testid="hero-image" />
            <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-md rounded-2xl p-4 border border-neutral-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-neutral-950 flex items-center justify-center">
                  <Brain className="w-6 h-6 text-sky-400" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-500">AI-Driven Learning</div>
                  <div className="font-display font-bold">Live + Real Projects</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
